/**
 * Document Retention Service
 * 
 * Manages data retention policies for documents.
 * Auto-deletes documents after retention period expires.
 */

import { db } from '@healthdesk/shared-services';
import { documentProcessingService } from './documentProcessingService';

// Retention periods (in days)
const RETENTION_PERIODS = {
  default: 365, // 1 year default
  phi: 2555, // 7 years for PHI (HIPAA requirement)
  guidelines: 730, // 2 years for clinical guidelines
  protocols: 1095, // 3 years for protocols
};

class DocumentRetentionService {
  /**
   * Get retention period for a document
   * @param {Object} document - Document metadata
   * @returns {number} Retention period in days
   */
  getRetentionPeriod(document) {
    if (document.metadata?.containsPHI) {
      return RETENTION_PERIODS.phi;
    }
    
    if (document.metadata?.documentType === 'guideline') {
      return RETENTION_PERIODS.guidelines;
    }
    
    if (document.metadata?.documentType === 'protocol') {
      return RETENTION_PERIODS.protocols;
    }
    
    return RETENTION_PERIODS.default;
  }

  /**
   * Check if document should be deleted
   * @param {Object} document - Document metadata
   * @returns {boolean} True if document should be deleted
   */
  shouldDelete(document) {
    if (!document.createdAt) return false;
    
    const retentionPeriod = this.getRetentionPeriod(document);
    const createdAt = document.createdAt.toDate ? document.createdAt.toDate() : new Date(document.createdAt);
    const expirationDate = new Date(createdAt);
    expirationDate.setDate(expirationDate.getDate() + retentionPeriod);
    
    return new Date() > expirationDate;
  }

  /**
   * Get expiration date for a document
   * @param {Object} document - Document metadata
   * @returns {Date} Expiration date
   */
  getExpirationDate(document) {
    if (!document.createdAt) return null;
    
    const retentionPeriod = this.getRetentionPeriod(document);
    const createdAt = document.createdAt.toDate ? document.createdAt.toDate() : new Date(document.createdAt);
    const expirationDate = new Date(createdAt);
    expirationDate.setDate(expirationDate.getDate() + retentionPeriod);
    
    return expirationDate;
  }

  /**
   * Delete expired documents
   * @param {string} baseQueryId - Optional: only delete for specific query
   * @param {number} position - Optional: only delete for specific position
   * @returns {Promise<number>} Number of documents deleted
   */
  async deleteExpiredDocuments(baseQueryId = null, position = null) {
    let query = db.collection('llmDocumentVectors');
    
    if (baseQueryId) {
      query = query.where('baseQueryId', '==', baseQueryId);
    }
    if (position !== null) {
      query = query.where('position', '==', position);
    }
    
    const snapshot = await query.get();
    let deletedCount = 0;
    
    for (const doc of snapshot.docs) {
      const document = { id: doc.id, ...doc.data() };
      
      if (this.shouldDelete(document)) {
        try {
          await documentProcessingService.deleteDocument(document.id);
          deletedCount++;
        } catch (error) {
          console.error(`[DocumentRetention] Failed to delete document ${document.id}:`, error);
        }
      }
    }
    
    return deletedCount;
  }

  /**
   * Get documents expiring soon
   * @param {number} daysAhead - Number of days to look ahead (default: 30)
   * @returns {Promise<Array>} Documents expiring soon
   */
  async getDocumentsExpiringSoon(daysAhead = 30) {
    const snapshot = await db.collection('llmDocumentVectors').get();
    const expiring = [];
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() + daysAhead);
    
    snapshot.docs.forEach(doc => {
      const document = { id: doc.id, ...doc.data() };
      const expirationDate = this.getExpirationDate(document);
      
      if (expirationDate && expirationDate <= cutoffDate) {
        expiring.push({
          ...document,
          expirationDate,
          daysUntilExpiration: Math.ceil((expirationDate - new Date()) / (1000 * 60 * 60 * 24)),
        });
      }
    });
    
    return expiring.sort((a, b) => a.expirationDate - b.expirationDate);
  }
}

export const documentRetentionService = new DocumentRetentionService();
export default documentRetentionService;
